import React, {useEffect, useState} from 'react';
import {View, Text, StyleSheet, ActivityIndicator, TouchableOpacity, Image, Platform} from 'react-native';
import {useTranslation} from 'react-i18next';
import {spacing, borderRadius} from '../constants';
import {MATERIAL_ICONS_FONT} from '../styles/fonts';
import {authService} from '../services';
import {useAppSelector, useAppDispatch} from '../store';
import {setUser} from '../store/authSlice';
import {settingsService} from '../services';
import {useRouting} from '../hooks/useRouting';

// Curated picsum photo IDs - same as HomeScreen
const CURATED_PHOTO_IDS = [
  15, 28, 29, 36, 39, 42, 54, 65, 82, 106,
  110, 129, 142, 152, 164, 167, 193, 197, 200, 244,
  306, 318, 325, 349, 392, 399, 429, 433, 447, 452,
];

function getBackgroundPhoto(): string {
  const idKey = 'homeBackgroundPhotoId';
  // Only try localStorage on web
  if (Platform.OS === 'web') {
    try {
      const storedId = localStorage.getItem(idKey);
      if (storedId) {
        return `https://picsum.photos/id/${storedId}/1920/1080`;
      }
    } catch {}
  }
  const id = CURATED_PHOTO_IDS[Math.floor(Math.random() * CURATED_PHOTO_IDS.length)];
  return `https://picsum.photos/id/${id}/1920/1080`;
}

// Track globally to prevent re-attempts across remounts
const attemptedTokens = new Set<string>();

export default function VerifyEmailScreen() {
  const {t} = useTranslation();
  const {getSearchParam, navigateTo} = useRouting();
  const dispatch = useAppDispatch();
  const {isAuthenticated} = useAppSelector(state => state.auth);

  const [isVerifying, setIsVerifying] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backgroundPhoto] = useState(getBackgroundPhoto);

  const token = getSearchParam('token');

  useEffect(() => {
    if (!token) {
      setError(t('verifyEmail.invalidLink'));
      setIsVerifying(false);
      return;
    }

    // Check if we've already attempted this token
    if (attemptedTokens.has(token)) {
      setError(t('verifyEmail.invalidLink'));
      setIsVerifying(false);
      return;
    }
    attemptedTokens.add(token);

    verifyEmail(token);
  }, [token]);

  const verifyEmail = async (verificationToken: string) => {
    try {
      await authService.verifyEmail(verificationToken);
      setIsSuccess(true);

      // If user is logged in, refresh their user data to update emailVerified status
      if (isAuthenticated) {
        try {
          const updatedUser = await settingsService.getCurrentUser();
          dispatch(setUser(updatedUser));
        } catch {
          // Ignore - user data refresh is optional
        }
      }
    } catch (err: any) {
      const errorMessage = err.message || t('verifyEmail.failed');

      if (errorMessage.toLowerCase().includes('expired') || errorMessage.toLowerCase().includes('invalid') || errorMessage.toLowerCase().includes('used')) {
        setError(t('verifyEmail.invalidLink'));
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleContinue = () => {
    if (isAuthenticated) {
      navigateTo('/settings/account');
    } else {
      navigateTo('/');
    }
  };

  return (
    <View style={styles.container}>
      {/* Background Image */}
      <Image
        source={{uri: backgroundPhoto}}
        style={styles.backgroundImage}
        resizeMode="cover"
      />

      {/* Gradient Overlay */}
      <View style={styles.gradientOverlay} />

      {/* Header */}
      <View style={styles.header}>
        <View style={styles.logoContainer}>
          <Image
            source={Platform.OS === 'web' ? {uri: '/icon.png'} : require('../assets/icon.png')}
            style={styles.logoImage}
            resizeMode="contain"
          />
          <Text style={styles.logoText}>{t('home.appName')}</Text>
        </View>
      </View>

      {/* Center Card */}
      <View style={styles.cardContainer}>
        <View style={styles.card}>
          {isVerifying ? (
            <>
              <ActivityIndicator size="large" color="#ee8c2b" />
              <Text style={styles.title}>{t('verifyEmail.title')}</Text>
              <Text style={styles.message}>{t('verifyEmail.verifying')}</Text>
            </>
          ) : isSuccess ? (
            <>
              <View style={styles.successIconContainer}>
                <Text style={styles.materialIcon}>check_circle</Text>
              </View>
              <Text style={styles.title}>{t('verifyEmail.success')}</Text>
              <Text style={styles.message}>{t('verifyEmail.successDescription')}</Text>
              <TouchableOpacity
                style={styles.primaryButton}
                onPress={handleContinue}
                activeOpacity={0.8}>
                <Text style={styles.primaryButtonText}>
                  {isAuthenticated ? t('verifyEmail.goToSettings') : t('common.back')}
                </Text>
              </TouchableOpacity>
            </>
          ) : (
            <>
              <View style={styles.errorIconContainer}>
                <Text style={styles.materialIconError}>error</Text>
              </View>
              <Text style={styles.title}>{t('verifyEmail.failed')}</Text>
              <Text style={styles.errorMessage}>{error}</Text>
              <TouchableOpacity
                style={styles.primaryButton}
                onPress={handleContinue}
                activeOpacity={0.8}>
                <Text style={styles.primaryButtonText}>
                  {isAuthenticated ? t('verifyEmail.goToSettings') : t('common.back')}
                </Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  backgroundImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
  },
  gradientOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoImage: {
    width: 40,
    height: 40,
    marginRight: spacing.sm,
    borderRadius: 8,
  },
  logoText: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 20,
    fontWeight: '700',
    color: '#fff',
  },
  cardContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xl,
  },
  card: {
    backgroundColor: 'rgba(255, 250, 245, 0.95)',
    borderRadius: 40,
    padding: spacing.xl,
    width: '100%',
    maxWidth: 420,
    alignItems: 'center',
    // @ts-ignore - web-specific style
    backdropFilter: 'blur(20px)',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 10},
    shadowOpacity: 0.3,
    shadowRadius: 30,
    elevation: 20,
  },
  successIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  errorIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(220, 38, 38, 0.15)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  materialIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 48,
    color: '#22c55e',
  },
  materialIconError: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 48,
    color: '#dc2626',
  },
  title: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 24,
    fontWeight: '700',
    color: '#221910',
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  message: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 15,
    fontWeight: '400',
    color: '#666',
    textAlign: 'center',
    marginTop: spacing.sm,
    lineHeight: 22,
  },
  errorMessage: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 15,
    fontWeight: '400',
    color: '#dc2626',
    textAlign: 'center',
    marginTop: spacing.sm,
    lineHeight: 22,
  },
  primaryButton: {
    backgroundColor: '#ee8c2b',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xl,
    borderRadius: borderRadius.md,
    minHeight: 56,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: spacing.xl,
    shadowColor: '#ee8c2b',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 4,
  },
  primaryButtonText: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
});
